import { useEffect, useState } from "react";
import { KfAvatar } from "./KfAvatar";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Trash2, Send } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

// Inline comment thread for a post — lazy-loaded the first time it's expanded (KfPostCard
// toggles `open`), with its own add-comment input and per-comment delete (author-only).
export function KfCommentSection({ postId, myUserId, onCommentCountChange }) {
  const [comments, setComments] = useState(null); // null = not loaded yet
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    api.get(`/kf/posts/${postId}/comments`).then((r) => setComments(r.data));
  }, [postId]);

  const submit = async () => {
    if (!draft.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/kf/posts/${postId}/comments`, { content: draft });
      setComments((prev) => [...(prev || []), data]);
      setDraft("");
      onCommentCountChange?.(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't post that comment");
    } finally {
      setPosting(false);
    }
  };

  const remove = async (commentId) => {
    try {
      await api.delete(`/kf/comments/${commentId}`);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      onCommentCountChange?.(-1);
    } catch {
      toast.error("Couldn't delete that comment");
    }
  };

  return (
    <div data-testid={`kf-comments-${postId}`} className="pt-3 border-t border-white/10 space-y-3">
      {comments === null ? (
        <p className="text-xs text-[#f1e0c5]/40">Loading comments…</p>
      ) : comments.length === 0 ? (
        <p className="text-xs text-[#f1e0c5]/40">No comments yet — be the first to reply.</p>
      ) : (
        <div className="space-y-2.5">
          {comments.map((c) => (
            <div key={c.id} data-testid={`kf-comment-${c.id}`} className="flex items-start gap-2.5">
              <KfAvatar name={c.author.name} avatarUrl={c.author.avatar_url} isMerchant={c.author.is_merchant} size="sm" />
              <div className="flex-1 min-w-0 bg-black/25 rounded-lg px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold">{c.author.name}</p>
                  {c.author.id === myUserId && (
                    <button data-testid={`kf-comment-delete-${c.id}`} onClick={() => remove(c.id)} className="text-[#f1e0c5]/30 hover:text-[#D90429]">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <p className="text-xs text-[#f1e0c5]/75 whitespace-pre-wrap">{c.content}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input
          data-testid={`kf-comment-input-${postId}`}
          placeholder="Reply to this Knight…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          className="bg-black/30 border-white/10 text-xs h-8"
          maxLength={1000}
        />
        <Button data-testid={`kf-comment-submit-${postId}`} size="icon" disabled={posting || !draft.trim()} onClick={submit} className="h-8 w-8 rounded-full bg-[#D4AF37] hover:bg-[#c19b2e] text-black shrink-0">
          <Send className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}
